(function () {
  const CM_PER_IN = 2.54;
  const cmToIn = (cm) => cm / CM_PER_IN;
  const inToCm = (inch) => inch * CM_PER_IN;

  const num = (v) => {
    const n = parseFloat(String(v ?? "").replace(/[^\d.]/g, ""));
    return Number.isFinite(n) ? n : null;
  };

  const format = (n) => {
    if (n === null || n === undefined) return "—";
    const fixed = Math.round(n * 10) / 10;
    return String(fixed);
  };

  function parseCSV(raw) {
    if (!raw) return null;

    let txt = raw;
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === "string") txt = parsed;
      else if (parsed && typeof parsed === "object" && Array.isArray(parsed.rows)) return parsed;
    } catch (_) {}

    txt = String(txt).trim();
    const lines = txt
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    if (lines.length < 2) return null;

    const headerLine = lines[0];
    const sep = headerLine.includes(",")
      ? ","
      : headerLine.includes("\t")
      ? "\t"
      : headerLine.includes(";")
      ? ";"
      : null;

    if (!sep) return null;

    const headers = headerLine.split(sep).map((h) => h.trim().toLowerCase());
    const rows = lines.slice(1).map((line) => {
      const cols = line.split(sep).map((c) => c.trim());
      const obj = {};
      headers.forEach((h, i) => (obj[h] = cols[i] ?? ""));
      return obj;
    });

    return { headers, rows };
  }

  function getHeaderKey(headers, want) {
    const w = want.toLowerCase();
    return (
      headers.find((h) => h === w) ||
      headers.find((h) => h.replace(/\s+/g, "") === w.replace(/\s+/g, "")) ||
      headers.find((h) => h.includes(w) || w.includes(h)) ||
      null
    );
  }

  function initModal(modal) {
    const inner = modal.querySelector("[data-sizechart-modal]");
    const sectionId = inner?.dataset.sectionId;
    if (!sectionId) return;

    const rawEl = modal.querySelector(`#sc-raw-${sectionId}`);
    const closeBtn = modal.querySelector(`#ModalClose-SizeChart-${sectionId}`);

    // Recommendations inside modal + opener
    const recoEl = modal.querySelector("[data-reco]");
    const recoWrap = modal.querySelector(".sc-reco");

    const scopeRoot = document.querySelector(`#MainProduct-${sectionId}`) || document;
    const openerRecoWrap = scopeRoot.querySelector(".sizechart-btn__reco");
    const openerRecoSize = scopeRoot.querySelector("[data-reco-inline]");

    function hideRecoEverywhere() {
      if (recoEl) recoEl.textContent = "";
      if (recoWrap) recoWrap.hidden = true;

      if (openerRecoSize) openerRecoSize.textContent = "";
      if (openerRecoWrap) openerRecoWrap.hidden = true;
    }

    function showRecoEverywhere(sizeText) {
      if (!sizeText) return hideRecoEverywhere();

      if (recoEl) recoEl.textContent = sizeText;
      if (recoWrap) recoWrap.hidden = false;

      if (openerRecoSize) openerRecoSize.textContent = sizeText;
      if (openerRecoWrap) openerRecoWrap.hidden = false;
    }

    hideRecoEverywhere();

    const parsed = parseCSV(rawEl ? rawEl.textContent : "");

    // ---- Close modal (Dawn-safe)
    const doClose = () => {
      modal.removeAttribute("open");
      modal.classList.remove("active");
      document.body.classList.remove("overflow-hidden");
    };

    if (closeBtn) {
      closeBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        doClose();
      });
    }

    modal.addEventListener("click", (e) => {
      const content = modal.querySelector(".product-popup-modal__content");
      if (!content) return;
      if (content.contains(e.target)) return;
      doClose();
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && modal.hasAttribute("open")) doClose();
    });

    // ---- Tabs
    const tabs = modal.querySelectorAll(".sizechart-tab");
    const panels = modal.querySelectorAll(".sizechart-panel");

    function activateTab(name) {
      tabs.forEach((t) => {
        const active = t.dataset.tab === name;
        t.classList.toggle("is-active", active);
        t.setAttribute("aria-selected", active ? "true" : "false");
      });

      panels.forEach((p) => p.classList.toggle("is-active", p.dataset.panel === name));

      if (name === "chart") {
        const activeUnitBtn = modal.querySelector('[data-panel="chart"] [data-table-unit].is-active');
        renderTable(activeUnitBtn ? activeUnitBtn.dataset.tableUnit : "cm");
      }
    }

    tabs.forEach((tab) => {
      tab.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        activateTab(tab.dataset.tab);
      });
    });

    // ---- Table
    const tableRoot = modal.querySelector('[data-panel="chart"] [data-table]');

    function renderTable(unit) {
      if (!tableRoot) return;

      if (!parsed || !parsed.rows?.length) {
        tableRoot.innerHTML =
          '<p style="padding:16px;">Size chart data is invalid. Paste CSV like:<br><code>Size,Shoulder,Sleeves Length,Bust,Waist,Hips,Length</code></p>';
        return;
      }

      const cols = [
        { label: "Size", key: "size", isSize: true },
        { label: "Shoulder", key: "shoulder" },
        { label: "Sleeves Length", key: "sleeves length" },
        { label: "Bust", key: "bust" },
        { label: "Waist", key: "waist" },
        { label: "Hips", key: "hips" },
        { label: "Length", key: "length" },
      ];

      const ths = cols.map((c) => `<th>${c.label}</th>`).join("");

      const trs = parsed.rows
        .map((row) => {
          return (
            "<tr>" +
            cols
              .map((c) => {
                const hk = getHeaderKey(parsed.headers, c.key);
                const raw = hk ? row[hk] : "";

                if (c.isSize) return `<td><strong>${raw || "—"}</strong></td>`;

                const v = num(raw);
                if (v === null) return "<td>—</td>";

                const out = unit === "in" ? cmToIn(v) : v;
                return `<td>${format(out)}</td>`;
              })
              .join("") +
            "</tr>"
          );
        })
        .join("");

      tableRoot.innerHTML = `
        <div class="sc-table-scroll">
          <table class="sc-table-native">
            <thead><tr>${ths}</tr></thead>
            <tbody>${trs}</tbody>
          </table>
        </div>
      `;
    }

    modal.querySelectorAll('[data-panel="chart"] [data-table-unit]').forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        modal
          .querySelectorAll('[data-panel="chart"] [data-table-unit]')
          .forEach((b) => {
            b.classList.remove("is-active");
            b.setAttribute("aria-selected", "false");
          });

        btn.classList.add("is-active");
        btn.setAttribute("aria-selected", "true");
        renderTable(btn.dataset.tableUnit);
      });
    });

    // ---- Calculator
    let currentUnit = "cm";
    const unitBtns = modal.querySelectorAll(".sc-unit");
    const suffixes = modal.querySelectorAll("[data-suffix]");

    const fields = {
      sleeves: modal.querySelector('[data-field="sleeves"]'),
      bust: modal.querySelector('[data-field="bust"]'),
      waist: modal.querySelector('[data-field="waist"]'),
      fit: modal.querySelector('[data-field="fit"]'),
    };

    function setSuffix(unit) {
      suffixes.forEach((s) => (s.textContent = unit === "in" ? "in" : "cm"));
    }

    function convertInputs(nextUnit) {
      if (nextUnit === currentUnit) return;

      ["sleeves", "bust", "waist"].forEach((k) => {
        const el = fields[k];
        if (!el) return;

        const v = num(el.value);
        if (v === null) return;

        const converted = nextUnit === "in" ? cmToIn(v) : inToCm(v);
        el.value = format(converted);
      });

      currentUnit = nextUnit;
      setSuffix(currentUnit);
      calcRecommend();
    }

    unitBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        unitBtns.forEach((b) => {
          b.classList.remove("is-active");
          b.setAttribute("aria-selected", "false");
        });

        btn.classList.add("is-active");
        btn.setAttribute("aria-selected", "true");

        convertInputs(btn.dataset.unit);
      });
    });

    setSuffix(currentUnit);

    // ✅ FIXED RECOMMENDATION (tie => bigger size)
    function calcRecommend() {
      if (!parsed || !parsed.rows?.length) {
        hideRecoEverywhere();
        return;
      }

      const s = num(fields.sleeves?.value);
      const b = num(fields.bust?.value);
      const w = num(fields.waist?.value);
      const fit = fields.fit?.value || "regular";

      if (s === null && b === null && w === null) {
        hideRecoEverywhere();
        return;
      }

      const sleevesCm = s === null ? null : currentUnit === "in" ? inToCm(s) : s;
      const bustCm = b === null ? null : currentUnit === "in" ? inToCm(b) : b;
      const waistCm = w === null ? null : currentUnit === "in" ? inToCm(w) : w;

      let bias = 0;
      if (fit === "tight") bias = -1;
      if (fit === "loose") bias = 1;

      const sizeKey = getHeaderKey(parsed.headers, "size") || "size";
      const sleevesKey =
        getHeaderKey(parsed.headers, "sleeves length") ||
        getHeaderKey(parsed.headers, "sleeves") ||
        "sleeves length";
      const bustKey = getHeaderKey(parsed.headers, "bust") || "bust";
      const waistKey = getHeaderKey(parsed.headers, "waist") || "waist";

      const scored = parsed.rows.map((row, idx) => {
        const size = String(row[sizeKey] ?? "").trim() || "";
        const rowSleeves = num(row[sleevesKey]);
        const rowBust = num(row[bustKey]);
        const rowWaist = num(row[waistKey]);

        let score = 0;
        let used = 0;

        if (sleevesCm !== null && rowSleeves !== null) {
          score += Math.abs(rowSleeves - sleevesCm);
          used++;
        }
        if (bustCm !== null && rowBust !== null) {
          score += Math.abs(rowBust - bustCm);
          used++;
        }
        if (waistCm !== null && rowWaist !== null) {
          score += Math.abs(rowWaist - waistCm);
          used++;
        }

        // ✅ if tie, prefer NOT smaller size:
        // if row is smaller than user measurement, add small penalty
        if (bustCm !== null && rowBust !== null && rowBust < bustCm) score += 0.5;
        if (waistCm !== null && rowWaist !== null && rowWaist < waistCm) score += 0.5;
        if (sleevesCm !== null && rowSleeves !== null && rowSleeves < sleevesCm) score += 0.5;

        if (used === 0) score = 999999;
        return { idx, size, score };
      });

      // ✅ MAIN FIX: if score tie -> choose bigger idx (bigger size)
      scored.sort((a, b) => {
        if (a.score === b.score) return b.idx - a.idx;
        return a.score - b.score;
      });

      let best = scored[0];

      if (!best || !best.size) {
        hideRecoEverywhere();
        return;
      }

      // apply fit bias
      const biasedIdx = best.idx + bias;
      if (biasedIdx >= 0 && biasedIdx < parsed.rows.length) {
        const maybe = String(parsed.rows[biasedIdx][sizeKey] ?? "").trim();
        if (maybe) best = { ...best, size: maybe };
      }

      const finalSize = String(best.size || "").trim();

      if (!finalSize || finalSize === "—" || finalSize === "-" || finalSize.toLowerCase() === "na") {
        hideRecoEverywhere();
        return;
      }

      showRecoEverywhere(finalSize);
    }

    ["input", "change"].forEach((evt) => {
      ["sleeves", "bust", "waist"].forEach((k) => fields[k]?.addEventListener(evt, calcRecommend));
      fields.fit?.addEventListener(evt, calcRecommend);
    });

    // activateTab("calc");
    activateTab("chart");
    renderTable("cm");
  }

  function boot() {
    document.querySelectorAll('modal-dialog[id^="SizeChartModal-"]').forEach((modal) => {
      if (modal.dataset.scInit === "1") return;
      modal.dataset.scInit = "1";
      initModal(modal);
    });
  }

  document.addEventListener("DOMContentLoaded", boot);
  document.addEventListener("shopify:section:load", boot);
})();